"use client"

import { useEffect, useMemo, useState } from "react"
import { Badge, Button, Form, InputGroup, ListGroup, Modal, OverlayTrigger, Popover, Spinner } from "react-bootstrap"
import { AlertTriangle, Check, Search, Star } from "lucide-react"
import { useTranslation } from "../../../hooks/use-translation"
import { softoneSearchKey, type SoftoneLink, type SoftoneSuggestion } from "../../../data/softone"

export interface SoftoneDetailRow {
  label: string
  value: string
}

interface SoftoneLinkBadgeProps {
  link: SoftoneLink | null | undefined
  suggestion: SoftoneSuggestion | null | undefined
  note?: string
  // the link no longer fits (user moved, or the SoftOne record disappeared)
  warning?: string
  approving?: boolean
  onApproveSuggestion?: () => void
  // how to show a SoftOne code, e.g. only the apartment number on the Users page
  formatCode?: (code: string) => string
  // extra popover rows for a SoftOne record, e.g. the apartment's contract period
  recordDetails?: (prjc: string) => SoftoneDetailRow[]
  // admin uid (linkedBy) → name
  adminName?: (uid: string) => string | undefined
}

/** Table cell: linked / no link / suggestion (with one-click approve) / pending.
 *  Hover or focus shows the whole assignment in a popover. */
export function SoftoneLinkBadge({
  link,
  suggestion,
  note,
  warning,
  approving,
  onApproveSuggestion,
  formatCode = (code) => code,
  recordDetails,
  adminName,
}: SoftoneLinkBadgeProps) {
  const { t, language } = useTranslation()

  const recordRows = (prjc: string | undefined, code: string | undefined, name: string | undefined) => [
    { label: t("softoneDetailCode"), value: code ?? "" },
    { label: t("softoneDetailName"), value: name ?? "" },
    ...(prjc && recordDetails ? recordDetails(prjc) : []),
  ]

  const decisionRows = (decided: SoftoneLink) => {
    // backfill runs are tagged "backfill-<date>" instead of an admin uid
    const by = decided.linkedBy && !decided.linkedBy.startsWith("backfill") ? adminName?.(decided.linkedBy) : undefined
    return [
      ...(decided.source ? [{ label: t("softoneDetailSource"), value: t(`softoneSource_${decided.source}`) }] : []),
      ...(by ? [{ label: t("softoneDetailBy"), value: by }] : []),
      ...(decided.linkedAt
        ? [{ label: t("softoneDetailAt"), value: new Date(decided.linkedAt).toLocaleString(language === "el" ? "el-GR" : "en-GB") }]
        : []),
    ]
  }

  let title: string
  let rows: SoftoneDetailRow[]
  if (link?.status === "linked") {
    title = t("softoneFilterLinked")
    rows = [
      ...recordRows(link.softonePrjc, link.softoneCode, link.softoneName),
      ...(link.confidence ? [{ label: t("softoneDetailMatch"), value: t(`softoneConfidence_${link.confidence}`) }] : []),
      ...decisionRows(link),
    ]
  } else if (link?.status === "none") {
    title = t("softoneNone")
    rows = decisionRows(link)
  } else if (suggestion) {
    title = t("softoneSuggested")
    rows = [
      ...recordRows(suggestion.softonePrjc, suggestion.softoneCode, suggestion.softoneName),
      { label: t("softoneDetailMatch"), value: t(`softoneConfidence_${suggestion.confidence}`) },
    ]
  } else {
    title = t("softonePending")
    rows = []
  }

  const popover = (
    <Popover id="softone-link-popover">
      <Popover.Header as="h3">{title}</Popover.Header>
      <Popover.Body>
        {rows.filter((row) => row.value).map((row) => (
          <div key={row.label} className="mb-1">
            <span className="text-muted">{row.label}:</span> {row.value}
          </div>
        ))}
        {!link && !suggestion && note && <div className="text-muted">{note}</div>}
        {warning && (
          <div className="text-warning mt-2">
            <AlertTriangle size={14} className="me-1" />
            {warning}
          </div>
        )}
      </Popover.Body>
    </Popover>
  )

  // the badge is the popover target; tabIndex makes it reachable with the keyboard too
  const withPopover = (badge: JSX.Element) => (
    <OverlayTrigger placement="auto" trigger={["hover", "focus"]} overlay={popover}>
      <span tabIndex={0} className="d-inline-flex align-items-center gap-1">
        {badge}
      </span>
    </OverlayTrigger>
  )

  if (link?.status === "linked") {
    return withPopover(
      <>
        <Badge bg="success">{formatCode(link.softoneCode ?? "")}</Badge>
        {warning && <AlertTriangle size={16} className="text-warning" aria-label={warning} />}
      </>,
    )
  }

  if (link?.status === "none") {
    return withPopover(<Badge bg="secondary">{t("softoneNoneShort")}</Badge>)
  }

  if (suggestion) {
    return (
      <span className="d-inline-flex align-items-center gap-1 text-nowrap">
        {withPopover(
          <Badge
            bg={suggestion.confidence === "fuzzy" ? "danger" : "warning"}
            text={suggestion.confidence === "fuzzy" ? undefined : "dark"}
          >
            {t("softoneSuggested")}: {formatCode(suggestion.softoneCode)}
          </Badge>,
        )}
        {onApproveSuggestion && (
          <Button
            variant="outline-success"
            size="sm"
            className="py-0 px-1"
            onClick={onApproveSuggestion}
            disabled={approving}
            title={t("softoneApproveSuggestion")}
            aria-label={t("softoneApproveSuggestion")}
          >
            {approving ? <Spinner animation="border" size="sm" /> : <Check size={14} />}
          </Button>
        )}
      </span>
    )
  }

  return withPopover(
    <Badge bg="light" text="dark" className="border">
      {t("softonePending")}
    </Badge>,
  )
}

interface SoftoneLinkSelectProps {
  // "" = pending, "none" = no link, otherwise a SoftOne prjc
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  suggestion: SoftoneSuggestion | null | undefined
  note?: string
  warning?: string
  disabled?: boolean
  loading?: boolean
  formatCode?: (code: string) => string
}

/** Modal field: pick the SoftOne record, explicitly "no link", or leave it pending. */
export function SoftoneLinkSelect({
  value,
  onChange,
  options,
  suggestion,
  note,
  warning,
  disabled,
  loading,
  formatCode = (code) => code,
}: SoftoneLinkSelectProps) {
  const { t } = useTranslation()
  const [showPicker, setShowPicker] = useState(false)

  const selectedLabel =
    value === ""
      ? t("softonePendingOption")
      : value === "none"
        ? t("softoneNone")
        : (options.find((option) => option.value === value)?.label ?? value)

  return (
    <Form.Group className="mb-3">
      <Form.Label>{t("softoneLinkLabel")}</Form.Label>
      {loading ? (
        <div className="py-2">
          <Spinner animation="border" size="sm" className="me-2" />
          {t("softoneLoading")}
        </div>
      ) : (
        <>
          <InputGroup>
            <Form.Control value={selectedLabel} title={selectedLabel} readOnly disabled={disabled} />
            <Button variant="outline-primary" onClick={() => setShowPicker(true)} disabled={disabled}>
              <Search size={16} className="me-1" />
              {t("softoneChoose")}
            </Button>
          </InputGroup>
          <SoftoneLinkPickerModal
            show={showPicker}
            onHide={() => setShowPicker(false)}
            value={value}
            onSelect={onChange}
            options={options}
            suggestion={suggestion}
          />
          {suggestion ? (
            <Form.Text className="d-block">
              {t("softoneSuggested")}: <strong>{formatCode(suggestion.softoneCode)}</strong> ({t(`softoneConfidence_${suggestion.confidence}`)})
              {value !== suggestion.softonePrjc && !disabled && (
                <Button variant="link" size="sm" className="p-0 ms-2 align-baseline" onClick={() => onChange(suggestion.softonePrjc)}>
                  {t("softoneUseSuggestion")}
                </Button>
              )}
            </Form.Text>
          ) : (
            note && <Form.Text className="d-block text-muted">{note}</Form.Text>
          )}
          {warning && (
            <Form.Text className="d-block text-warning">
              <AlertTriangle size={14} className="me-1" />
              {warning}
            </Form.Text>
          )}
          <Form.Text className="d-block text-muted">{t("softoneAdminOnlyHelp")}</Form.Text>
        </>
      )}
    </Form.Group>
  )
}

interface SoftoneLinkPickerModalProps {
  show: boolean
  onHide: () => void
  value: string
  onSelect: (value: string) => void
  options: { value: string; label: string }[]
  suggestion: SoftoneSuggestion | null | undefined
}

/** Picker opened over the edit modal: search by code or name; the suggestion comes first. */
function SoftoneLinkPickerModal({ show, onHide, value, onSelect, options, suggestion }: SoftoneLinkPickerModalProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")

  useEffect(() => {
    if (!show) setQuery("")
  }, [show])

  const sorted = useMemo(
    () =>
      options
        .map((option) => ({ ...option, key: softoneSearchKey(option.label) }))
        .sort((a, b) => {
          const aSuggested = a.value === suggestion?.softonePrjc
          const bSuggested = b.value === suggestion?.softonePrjc
          if (aSuggested !== bSuggested) return aSuggested ? -1 : 1
          return a.label.localeCompare(b.label, "el", { numeric: true })
        }),
    [options, suggestion],
  )

  // every word of the query must appear, so "ΓΜ06" and "1009 06" both work
  const terms = query.split(/\s+/).map(softoneSearchKey).filter(Boolean)
  const filtered = terms.length ? sorted.filter((option) => terms.every((term) => option.key.includes(term))) : sorted

  const select = (next: string) => {
    onSelect(next)
    onHide()
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      className="softone-picker-modal"
      backdropClassName="softone-picker-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("softoneLinkLabel")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InputGroup className="mb-3">
          <InputGroup.Text className="bg-white border-end-0">
            <Search size={16} className="text-muted" />
          </InputGroup.Text>
          <Form.Control
            type="search"
            autoFocus
            placeholder={t("softoneSearchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && filtered.length > 0) {
                e.preventDefault()
                select(filtered[0].value)
              }
            }}
            className="border-start-0"
          />
        </InputGroup>

        <ListGroup>
          {terms.length === 0 && (
            <>
              <ListGroup.Item action active={value === ""} onClick={() => select("")}>
                {t("softonePendingOption")}
              </ListGroup.Item>
              <ListGroup.Item action active={value === "none"} onClick={() => select("none")}>
                {t("softoneNone")}
              </ListGroup.Item>
            </>
          )}
          {filtered.map((option) => (
            <ListGroup.Item
              key={option.value}
              action
              active={value === option.value}
              onClick={() => select(option.value)}
              className="d-flex align-items-center justify-content-between gap-2"
            >
              <span>{option.label}</span>
              {option.value === suggestion?.softonePrjc && (
                <Badge bg="warning" text="dark" className="d-inline-flex align-items-center gap-1 text-nowrap">
                  <Star size={12} />
                  {t("softoneSuggested")}
                </Badge>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
        {terms.length > 0 && filtered.length === 0 && (
          <div className="text-center py-4 text-muted">{t("softoneNoResults")}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="modal-cancel-btn" onClick={onHide}>
          {t("cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
